import axios from "axios";
import jwtDecode from "jwt-decode";
import { SERVER_URL } from "../ServerLink";
import { ACTIVITY_KEY as SESSION_ACTIVITY_KEY, getIdleLimitMs } from "../../utils/sessionConfig";
import { computeRefreshDelayMs, decodeJwtExpMs, isWithinRenewalWindow } from "./tokenRefreshTiming";

const REFRESH_PATH = "auth/refresh";
const LOGOUT_PATH = "auth/logout";
const REFRESH_KEY = "refreshToken";
const ACCESS_KEY = "accessToken";
const ACCESS_TTL_KEY = "accessExpiresInSec";
const RECOVERY_KEY = "rdc-session-lock";
const RENEWED_KEY = "rdc-session-renewed";
const api = axios.create({ baseURL: SERVER_URL + "/", timeout: 60 * 1000 });

function getAccessToken() { return window.localStorage.getItem(ACCESS_KEY) || ""; }
function getRefreshToken() { return window.localStorage.getItem(REFRESH_KEY) || ""; }
function identityOf(token) {
    try {
        const p = jwtDecode(token);
        const id = p.id || p._id || p.sub;
        const normalized = String(p.role || "").toLowerCase().replace(/[_ ]/g, "");
        const role = ({ owner: "company", propertymanager: "manager" })[normalized] || normalized;
        return id && role ? id + ":" + role : "";
    } catch (_) { return ""; }
}
function emailOf(token) {
    try { return jwtDecode(token).email || ""; } catch (_) { return ""; }
}
function storedEmail() {
    try { return JSON.parse(window.localStorage.getItem("authUser") || "{}").email || ""; }
    catch (_) { return ""; }
}

let tabAccessToken = getAccessToken();
let tabEmail = emailOf(tabAccessToken) || storedEmail();
let sessionVersion = 0;
let refreshInFlight = null;
let proactiveTimer = null;
let recoveryState = null;
let recoveryToken = "";
let recoveryWaiters = [];
const recoveryListeners = new Set();
try {
    const saved = JSON.parse(window.sessionStorage.getItem(RECOVERY_KEY) || "null");
    if (saved && getAccessToken() && window.localStorage.getItem("authUser")) {
        recoveryState = { reason: saved.reason || "session_expired", email: saved.email || tabEmail };
        recoveryToken = getAccessToken();
    }
} catch (_) { /* Disabled sessionStorage must not prevent signing in. */ }

function announceRecovery() { recoveryListeners.forEach(listener => listener(recoveryState)); }
function subscribeRecovery(listener) {
    recoveryListeners.add(listener);
    return () => recoveryListeners.delete(listener);
}
function sessionChangedError() {
    const error = new Error("Your session changed. Please try again.");
    error.code = "AUTH_SESSION_CHANGED";
    return error;
}
function lockSession(reason = "session_expired") {
    if (recoveryState || !window.localStorage.getItem("authUser")) return;
    recoveryToken = tabAccessToken || getAccessToken();
    recoveryState = { reason, email: emailOf(recoveryToken) || tabEmail };
    if (proactiveTimer) clearTimeout(proactiveTimer);
    try { window.sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(recoveryState)); } catch (_) {}
    announceRecovery();
}
function finishRecovery(error) {
    recoveryState = null;
    recoveryToken = "";
    try { window.sessionStorage.removeItem(RECOVERY_KEY); } catch (_) {}
    const waiters = recoveryWaiters;
    recoveryWaiters = [];
    announceRecovery();
    waiters.forEach(({ resolve, reject }) => error ? reject(error) : resolve(getAccessToken()));
}
function waitForRecovery(config) {
    if (!recoveryState) return Promise.resolve(getAccessToken());
    if (config && config.cancelToken) config.cancelToken.throwIfRequested();
    return new Promise((resolve, reject) => {
        const waiter = { resolve, reject };
        recoveryWaiters.push(waiter);
        if (config && config.cancelToken) config.cancelToken.promise.then(reason => {
            recoveryWaiters = recoveryWaiters.filter(item => item !== waiter);
            reject(reason);
        });
    });
}
function setTokens({ accessToken, refreshToken, accessExpiresIn, accessExpiresInSec }) {
    // Publish refresh first so other tabs see the complete new pair.
    if (refreshToken) window.localStorage.setItem(REFRESH_KEY, refreshToken);
    const ttl = accessExpiresIn || accessExpiresInSec;
    if (ttl) window.localStorage.setItem(ACCESS_TTL_KEY, String(ttl));
    if (accessToken) {
        window.localStorage.setItem(ACCESS_KEY, accessToken);
        tabAccessToken = accessToken;
        tabEmail = emailOf(accessToken) || storedEmail();
    }
    scheduleProactiveRefresh();
}
function clearTokens() {
    sessionVersion += 1;
    if (proactiveTimer) clearTimeout(proactiveTimer);
    [ACCESS_KEY, REFRESH_KEY, "authUser", "role", SESSION_ACTIVITY_KEY, ACCESS_TTL_KEY, RENEWED_KEY]
        .forEach(key => window.localStorage.removeItem(key));
    tabAccessToken = "";
    tabEmail = "";
    finishRecovery(sessionChangedError());
}
function markActivity() { window.localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now())); }
function getIdleMs() {
    const raw = window.localStorage.getItem(SESSION_ACTIVITY_KEY);
    const last = Number(raw);
    return raw !== null && Number.isFinite(last) ? Math.max(0, Date.now() - last) : -1;
}
export function isDefinitiveAuthError(error) {
    const status = error && error.response && error.response.status;
    return status === 401 || status === 403;
}
function requiresPassword(error) {
    return isDefinitiveAuthError(error) || (error && error.message === "NO_REFRESH_TOKEN");
}
function assertCurrentSession(identity) {
    if (identity && identityOf(getAccessToken()) !== identity) {
        lockSession("account_changed");
        throw sessionChangedError();
    }
}

async function doNetworkRefresh() {
    const rt = getRefreshToken();
    const identity = identityOf(tabAccessToken || getAccessToken());
    assertCurrentSession(identity);
    const version = sessionVersion;
    if (!rt) throw new Error("NO_REFRESH_TOKEN");
    const res = await axios.post(SERVER_URL + "/" + REFRESH_PATH,
        { refreshToken: rt, idleMs: getIdleMs() }, { timeout: 15 * 1000 });
    const data = res && res.data;
    // Never let a late response undo sign-out or overwrite a newer login.
    if (version !== sessionVersion || !getRefreshToken()) throw sessionChangedError();
    assertCurrentSession(identity);
    if (getRefreshToken() !== rt) {
        if (identityOf(getAccessToken()) !== identity) throw sessionChangedError();
        return getAccessToken();
    }
    if (!data || !data.accessToken || !data.refreshToken) throw new Error("BAD_REFRESH_RESPONSE");
    if (identity && identityOf(data.accessToken) !== identity) throw sessionChangedError();
    if (recoveryState) throw sessionChangedError();
    setTokens(data);
    return data.accessToken;
}
async function runRefreshWithLock() {
    const before = getRefreshToken();
    const identity = identityOf(tabAccessToken || getAccessToken());
    assertCurrentSession(identity);
    const version = sessionVersion;
    const task = async () => {
        if (version !== sessionVersion || recoveryState) throw sessionChangedError();
        assertCurrentSession(identity);
        const current = getRefreshToken();
        if (current && before && current !== before) {
            scheduleProactiveRefresh();
            return getAccessToken();
        }
        return doNetworkRefresh();
    };
    const locks = typeof navigator !== "undefined" && navigator.locks;
    // AbortController also bounds waiting in browsers without AbortSignal.timeout.
    if (locks && locks.request && typeof AbortController !== "undefined") {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10 * 1000);
        try {
            return await locks.request("rdc-token-refresh", { signal: controller.signal }, async () => {
                clearTimeout(timeout);
                return task();
            });
        } catch (err) {
            if (err && (err.name === "AbortError" || err.name === "TimeoutError")) return task();
            throw err;
        } finally { clearTimeout(timeout); }
    }
    return task();
}
async function performRefresh() {
    if (recoveryState) throw sessionChangedError();
    if (refreshInFlight) return refreshInFlight;
    if (!getRefreshToken()) throw new Error("NO_REFRESH_TOKEN");
    refreshInFlight = runRefreshWithLock().finally(() => { refreshInFlight = null; });
    return refreshInFlight;
}

async function reauthenticate({ password, otp, otpChannel }) {
    const token = recoveryToken || tabAccessToken || getAccessToken();
    const version = sessionVersion;
    const response = await axios.post(SERVER_URL + "/auth/reauthenticate",
        { accessToken: token, password, otp, otpChannel }, { timeout: 30 * 1000 });
    const data = response && response.data;
    if (version !== sessionVersion) throw sessionChangedError();
    if (data && data.requiresOtp && !data.accessToken) return data;
    if (!data || !data.accessToken || !data.refreshToken) {
        throw new Error((data && data.message) || "Unable to renew your session. Please try again.");
    }
    // Check the fixed identity before releasing writes from the open page.
    if (!identityOf(token) || identityOf(data.accessToken) !== identityOf(token)) {
        throw new Error("Please sign in with the same account to continue your work.");
    }
    sessionVersion += 1;
    const jwtInformation = data.jwtInformation || {};
    window.localStorage.setItem("authUser", JSON.stringify({
        ...jwtInformation, uid: data.uid || jwtInformation.id || jwtInformation._id,
        accessToken: data.accessToken,
        userData: data.userData || jwtInformation,
    }));
    if (jwtInformation.role) window.localStorage.setItem("role", jwtInformation.role);
    setTokens(data);
    markActivity();
    finishRecovery();
    // Background rotation cannot unlock an idle tab; password verification can.
    window.localStorage.setItem(RENEWED_KEY, JSON.stringify({ token: data.accessToken, at: Date.now() }));
    scheduleProactiveRefresh();
    window.dispatchEvent(new Event("rdc-auth-changed"));
    return data;
}
function forceLogoutRedirect(reason = "logout") {
    clearTokens();
    if (window.location.pathname !== "/login") {
        window.location.href = "/login?reason=" + encodeURIComponent(reason);
    }
}
async function endSession(reason = "logout") {
    if (reason === "session_expired" || reason === "session_timeout") {
        lockSession(reason);
        return;
    }
    const rt = getRefreshToken();
    const idleMs = getIdleMs();
    clearTokens();
    const logoutVersion = sessionVersion;
    try {
        if (rt) await axios.post(SERVER_URL + "/" + LOGOUT_PATH,
            { refreshToken: rt, reason, idleMs }, { timeout: 10 * 1000 });
    } catch (_) { /* Local sign-out still completes while offline. */ }
    finally {
        // Another tab may have signed in while revocation was in flight.
        if (logoutVersion === sessionVersion && !getAccessToken() && window.location.pathname !== "/login") {
            window.location.href = "/login?reason=" + encodeURIComponent(reason);
        }
    }
}
async function sendHeartbeat() {
    try {
        const idleMs = getIdleMs();
        if (recoveryState || !getRefreshToken() || idleMs < 0 || idleMs > getIdleLimitMs()) return;
        await api.post("auth/heartbeat", { idleMs, path: window.location.pathname });
    } catch (_) { /* Telemetry must not disrupt work. */ }
}
function isAuthEndpoint(config) {
    const url = (config && config.url) || "";
    const path = url.replace(/^https?:\/\/[^/]+/i, "").split("?")[0];
    return /(?:^|\/)(?:login|logout|auth\/(?:refresh|reauthenticate|logout|logout-all))\/?$/.test(path);
}
function isApiRequest(config) {
    try {
        const url = config.url || "";
        const absolute = /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(url);
        const target = new URL(absolute ? url : (config.baseURL || SERVER_URL).replace(/\/+$/, "") + "/" + url.replace(/^\/+/, ""), window.location.href);
        const server = new URL(SERVER_URL, window.location.href);
        const rootPath = server.pathname.replace(/\/+$/, "");
        return target.origin === server.origin && (target.pathname === rootPath || target.pathname.startsWith(rootPath + "/"));
    } catch (_) { return false; }
}
function assertRequestIdentity(config, token) {
    if (config.__sessionIdentity && config.__sessionIdentity !== identityOf(token)) {
        lockSession("account_changed");
        throw sessionChangedError();
    }
}
api.interceptors.request.use(async config => {
    if (!isApiRequest(config)) return config;
    if (!isAuthEndpoint(config)) {
        config.__sessionIdentity = config.__sessionIdentity || identityOf(tabAccessToken || getAccessToken());
        assertRequestIdentity(config, getAccessToken());
    }
    if (!isAuthEndpoint(config) && (getAccessToken() || recoveryState)) {
        if (recoveryState) await waitForRecovery(config);
        const exp = decodeJwtExpMs(getAccessToken());
        if (!config.__isRetry && exp && exp <= Date.now() + 30 * 1000) {
            try { await performRefresh(); }
            catch (error) {
                if (!requiresPassword(error)) throw error;
                lockSession("session_expired");
                if (!recoveryState) throw error;
                await waitForRecovery(config);
            }
        }
    }
    const token = getAccessToken();
    if (!isAuthEndpoint(config)) assertRequestIdentity(config, token);
    config.headers = config.headers || {};
    if (token) config.headers.Authorization = "Bearer " + token;
    return config;
});
api.interceptors.response.use(response => response, async error => {
    const original = error && error.config;
    const status = error && error.response && error.response.status;
    if (status !== 401 || !original || !isApiRequest(original) || original.__isRetry || isAuthEndpoint(original)) throw error;
    const sentWith = (original.headers && original.headers.Authorization) || "";
    original.__sessionIdentity = original.__sessionIdentity || identityOf(sentWith.replace(/^Bearer /, ""));
    assertRequestIdentity(original, getAccessToken());
    let newAccess;
    try {
        const current = getAccessToken();
        newAccess = current && "Bearer " + current !== sentWith && !recoveryState
            ? current : await performRefresh();
    } catch (refreshError) {
        if (refreshError && refreshError.code === "AUTH_SESSION_CHANGED" && !recoveryState) throw refreshError;
        if (recoveryState) newAccess = await waitForRecovery(original);
        else {
            const current = getAccessToken();
            if (current && "Bearer " + current !== sentWith) newAccess = current;
            else if (requiresPassword(refreshError)) {
                lockSession("session_expired");
                if (!recoveryState) throw refreshError;
                newAccess = await waitForRecovery(original);
            } else throw refreshError;
        }
    }
    assertRequestIdentity(original, newAccess);
    // Retry only explicit auth rejections, never ambiguous network write failures.
    original.__isRetry = true;
    original.headers = original.headers || {};
    original.headers.Authorization = "Bearer " + newAccess;
    return api.request(original);
});

function storedTtlMs() {
    return (parseInt(window.localStorage.getItem(ACCESS_TTL_KEY) || "0", 10) || 0) * 1000;
}
function userIsActive() {
    const idle = getIdleMs();
    return idle >= 0 && idle < getIdleLimitMs();
}
function scheduleProactiveRefresh() {
    if (proactiveTimer) clearTimeout(proactiveTimer);
    if (recoveryState || !getAccessToken()) return;
    const delay = computeRefreshDelayMs({ nowMs: Date.now(), expMs: decodeJwtExpMs(getAccessToken()), ttlMs: storedTtlMs() });
    proactiveTimer = setTimeout(async () => {
        try {
            if (userIsActive()) { await performRefresh(); return; }
        } catch (error) {
            if (requiresPassword(error)) lockSession("session_expired");
        }
        scheduleProactiveRefresh();
    }, delay);
}
async function renewOnWake() {
    if (recoveryState || !getAccessToken()) return;
    try {
        if (userIsActive() && isWithinRenewalWindow({
            nowMs: Date.now(), expMs: decodeJwtExpMs(getAccessToken()), ttlMs: storedTtlMs(),
        })) { await performRefresh(); return; }
    } catch (error) {
        if (requiresPassword(error)) lockSession("session_expired");
    }
    scheduleProactiveRefresh();
}
if (typeof window !== "undefined") {
    scheduleProactiveRefresh();
    window.addEventListener("rdc-auth-changed", () => {
        sessionVersion += 1;
        tabAccessToken = getAccessToken();
        tabEmail = emailOf(tabAccessToken) || storedEmail();
        scheduleProactiveRefresh();
    });
    window.addEventListener("storage", event => {
        if ((event.key === ACCESS_KEY || event.key === "authUser") && !event.newValue) {
            // Storage events may wait behind other work. Ignore a removal if a
            // newer login has already replaced that value in shared storage.
            if (window.localStorage.getItem(event.key)) return;
            // Automatic expiry retains identity. Key removal is explicit sign-out.
            forceLogoutRedirect("logout");
            return;
        }
        if (event.key === ACCESS_KEY && event.newValue) {
            if (identityOf(tabAccessToken) && identityOf(event.newValue) !== identityOf(tabAccessToken)) {
                sessionVersion += 1;
                lockSession("account_changed");
                return;
            }
            tabAccessToken = event.newValue;
            scheduleProactiveRefresh();
        }
        if (event.key === RENEWED_KEY && event.newValue && recoveryState) {
            try {
                const { token } = JSON.parse(event.newValue);
                if (token === getAccessToken() && identityOf(token) && identityOf(token) === identityOf(recoveryToken)) {
                    tabAccessToken = token;
                    sessionVersion += 1;
                    markActivity();
                    finishRecovery();
                    scheduleProactiveRefresh();
                }
            } catch (_) {}
        }
    });
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") renewOnWake();
    });
    window.addEventListener("focus", renewOnWake);
    window.addEventListener("online", renewOnWake);
}
export const auth = {
    getAccessToken, getRefreshToken, setTokens, clearTokens, markActivity,
    forceLogoutRedirect, endSession, performRefresh, sendHeartbeat,
    getRecoveryState: () => recoveryState, subscribeRecovery, lockSession, reauthenticate,
};
export default api;
